// FILE: src/pages/Dashboard.jsx
import React, { useEffect, useState } from "react";
import { db } from "../firebase";
import {
  collection,
  query,
  orderBy,
  getDocs,
  limit,
} from "firebase/firestore";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";

function smallCsvDownload(rows, headers = ["id", "name", "email", "phone"]) {
  if (!rows.length) return;
  const csvContent = [
    headers.join(","),
    ...rows.map((r) =>
      headers
        .map((h) => `"${String(r[h] ?? "").replace(/"/g, '""')}"`)
        .join(",")
    ),
  ].join("\n");

  const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  const ts = new Date().toISOString().slice(0, 19).replace(/[:T]/g, "-");
  a.href = url;
  a.download = `members-${ts}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

export default function Dashboard({ navigateToMembers = () => {} }) {
  const [total, setTotal] = useState(0);
  const [recent, setRecent] = useState([]);
  const [loading, setLoading] = useState(true);

  const [eventStats, setEventStats] = useState({
    totalEvents: 0,
    totalRsvps: 0,
    totalCheckIns: 0,
  });
  const [eventCards, setEventCards] = useState([]);
  const [publishedDevos, setPublishedDevos] = useState(0);

  const fetchMetrics = async () => {
    try {
      setLoading(true);

      // 👥 Members metrics
      const allQ = query(collection(db, "members"));
      const snapAll = await getDocs(allQ);
      setTotal(snapAll.size);

      // Recent 5 members – prefer createdAt, fallback to any 5
      try {
        const recentQ = query(
          collection(db, "members"),
          orderBy("createdAt", "desc"),
          limit(5)
        );
        const snapRecent = await getDocs(recentQ);
        setRecent(snapRecent.docs.map((d) => ({ id: d.id, ...d.data() })));
      } catch (err) {
        const snap = await getDocs(query(collection(db, "members"), limit(5)));
        setRecent(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
      }

      // 📅 Events metrics
      const eventsSnap = await getDocs(collection(db, "events"));

      let totalEvents = 0;
      let totalRsvps = 0;
      let totalCheckIns = 0;

      const eventsWithCounts = [];

      for (const d of eventsSnap.docs) {
        const ev = { id: d.id, ...d.data() };

        const rsvpSnap = await getDocs(collection(db, "events", ev.id, "rsvps"));
        const checkinSnap = await getDocs(
          collection(db, "events", ev.id, "checkins")
        );

        const rsvpCount = rsvpSnap.size;
        const checkinCount = checkinSnap.size;

        totalEvents += 1;
        totalRsvps += rsvpCount;
        totalCheckIns += checkinCount;

        eventsWithCounts.push({
          ...ev,
          rsvpCount,
          checkinCount,
        });
      }

      const sortedEvents = [...eventsWithCounts].sort((a, b) => {
        const aTime = a.startAt?.seconds
          ? a.startAt.seconds * 1000
          : Date.parse(a.startAt || 0);
        const bTime = b.startAt?.seconds
          ? b.startAt.seconds * 1000
          : Date.parse(b.startAt || 0);
        return aTime - bTime;
      });

      setEventStats({
        totalEvents,
        totalRsvps,
        totalCheckIns,
      });
      setEventCards(sortedEvents.slice(0, 6));

      // 📖 Devotionals metrics
      const devSnap = await getDocs(collection(db, "devotionals"));
      let publishedCount = 0;
      devSnap.forEach((docSnap) => {
        const data = docSnap.data() || {};
        const status = (data.status || "").toLowerCase();
        if (status === "published") {
          publishedCount += 1;
        }
      });
      setPublishedDevos(publishedCount);
    } catch (err) {
      console.error("Error fetching dashboard metrics:", err);
      setEventStats({ totalEvents: 0, totalRsvps: 0, totalCheckIns: 0 });
      setEventCards([]);
      setPublishedDevos(0);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMetrics();
  }, []);

  const formatDateTime = (ts) => {
    if (!ts) return "";
    if (ts.seconds) {
      return new Date(ts.seconds * 1000).toLocaleString();
    }
    try {
      return new Date(ts).toLocaleString();
    } catch {
      return "";
    }
  };

  const eventChartData = eventCards.map((ev) => ({
    name: (ev.title || "Event").slice(0, 12),
    rsvps: ev.rsvpCount || 0,
    checkIns: ev.checkinCount || 0,
  }));

  return (
    <div className="p-6 space-y-8">
      {/* PAGE HEADER */}
      <header className="flex items-center justify-between">
        <h1 className="text-2xl font-bold">Admin Dashboard</h1>
        <div className="flex gap-2">
          <button
            className="px-3 py-1 rounded border"
            onClick={() => {
              (async () => {
                const q = query(collection(db, "members"));
                const snap = await getDocs(q);
                const rows = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
                smallCsvDownload(rows);
              })();
            }}
          >
            Download Members CSV
          </button>
          <button
            className="px-3 py-1 rounded border"
            onClick={() => navigateToMembers()}
          >
            Open Members
          </button>
        </div>
      </header>

      {loading ? (
        <div>Loading metrics…</div>
      ) : (
        <>
          {/* 👥 MEMBERS OVERVIEW */}
          <section className="space-y-3">
            <div>
              <h2 className="text-xl font-semibold">Members overview</h2>
              <p className="text-sm text-gray-500">
                Membership totals and recent sign-ups.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="p-4 border rounded shadow-sm bg-white">
                <div className="text-sm text-gray-500">Total Members</div>
                <div className="text-3xl font-bold">{total}</div>
              </div>

              <div className="md:col-span-2 p-4 border rounded shadow-sm bg-white">
                <div className="flex items-center justify-between mb-3">
                  <div>
                    <div className="text-sm text-gray-500">Recent Members</div>
                  </div>
                  <button
                    className="px-2 py-1 text-sm border rounded"
                    onClick={fetchMetrics}
                  >
                    Refresh
                  </button>
                </div>

                {recent.length === 0 ? (
                  <div>No recent members</div>
                ) : (
                  <ul className="space-y-2">
                    {recent.map((r) => (
                      <li
                        key={r.id}
                        className="flex items-center justify-between"
                      >
                        <div>
                          <div className="font-medium">
                            {r.name ||
                              `${r.firstName || ""} ${r.lastName || ""}`.trim() ||
                              "—"}
                          </div>
                          <div className="text-sm text-gray-600">
                            {r.email || "—"}
                          </div>
                        </div>
                        <div className="text-sm text-gray-500">
                          {r.phone || r.cellNumber || "—"}
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
          </section>

          {/* 📅 EVENTS OVERVIEW */}
          <section className="space-y-4">
            <div>
              <h2 className="text-xl font-semibold">Events overview</h2>
              <p className="text-sm text-gray-500">
                RSVP and check-in activity across all events.
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div className="p-4 border rounded shadow-sm bg-white">
                <div className="text-xs text-gray-500 uppercase tracking-wide">
                  Total Events
                </div>
                <div className="text-2xl font-bold mt-1">
                  {eventStats.totalEvents}
                </div>
              </div>
              <div className="p-4 border rounded shadow-sm bg-white">
                <div className="text-xs text-gray-500 uppercase tracking-wide">
                  RSVPs
                </div>
                <div className="text-2xl font-bold mt-1 text-indigo-700">
                  {eventStats.totalRsvps}
                </div>
              </div>
              <div className="p-4 border rounded shadow-sm bg-white">
                <div className="text-xs text-gray-500 uppercase tracking-wide">
                  Check-ins
                </div>
                <div className="text-2xl font-bold mt-1 text-emerald-700">
                  {eventStats.totalCheckIns}
                </div>
              </div>
            </div>

            {eventChartData.length > 0 && (
              <div className="p-4 border rounded-xl shadow-sm bg-white h-72">
                <h3 className="text-sm font-semibold text-gray-700 mb-2">
                  RSVPs & Check-ins per event
                </h3>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart
                    data={eventChartData}
                    margin={{ top: 16, right: 16, left: -16, bottom: 0 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" vertical={false} />
                    <XAxis dataKey="name" tick={{ fontSize: 10 }} />
                    <YAxis tick={{ fontSize: 10 }} />
                    <Tooltip />
                    <Bar dataKey="rsvps" fill="#4f46e5" name="RSVPs" />
                    <Bar dataKey="checkIns" fill="#10b981" name="Check-ins" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}

            {eventCards.length === 0 ? (
              <div className="text-sm text-gray-500">
                No events found yet. Create events from the Manage Events
                screen.
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {eventCards.map((ev) => {
                  const rsvps = ev.rsvpCount || 0;
                  const checkIns = ev.checkinCount || 0;

                  return (
                    <div
                      key={ev.id}
                      className="p-4 border rounded-xl shadow-sm bg-white space-y-2"
                    >
                      <div>
                        <div className="font-semibold text-base">
                          {ev.title || "Untitled event"}
                        </div>
                        <div className="text-xs text-gray-500">
                          {formatDateTime(ev.startAt)}
                          {ev.location ? ` • ${ev.location}` : ""}
                        </div>
                      </div>

                      {ev.description && (
                        <p className="text-sm text-gray-600 line-clamp-2">
                          {ev.description}
                        </p>
                      )}

                      <div className="flex gap-2 mt-2">
                        <div className="flex-1 px-3 py-2 rounded-lg bg-indigo-50 text-indigo-700 text-center">
                          <div className="text-lg font-bold">{rsvps}</div>
                          <div className="text-[11px] uppercase tracking-wide">
                            RSVPs
                          </div>
                        </div>
                        <div className="flex-1 px-3 py-2 rounded-lg bg-emerald-50 text-emerald-700 text-center">
                          <div className="text-lg font-bold">{checkIns}</div>
                          <div className="text-[11px] uppercase tracking-wide">
                            Check-ins
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </section>

          {/* 📖 DEVOTIONALS OVERVIEW */}
          <section className="space-y-3">
            <div>
              <h2 className="text-xl font-semibold">Devotionals overview</h2>
              <p className="text-sm text-gray-500">
                Published daily devotionals available to members.
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 md:grid-cols-4 gap-4">
              <div className="p-4 border rounded shadow-sm bg-white">
                <div className="text-xs text-gray-500 uppercase tracking-wide">
                  Published Devotionals
                </div>
                <div className="text-2xl font-bold mt-1 text-purple-700">
                  {publishedDevos}
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  Manage devotionals under{" "}
                  <span className="font-medium">Manage Devotionals</span>.
                </p>
              </div>
            </div>
          </section>
        </>
      )}
    </div>
  );
}